import { newRelicNoticeError } from '../utils/newRelicActions';

export const LEAD_TOKEN_WAIT_MS = 3000;
/** How long submit waits for the TCPA disclosure to mount before giving up. */
export const JORNAYA_DISCLOSURE_WAIT_MS = LEAD_TOKEN_WAIT_MS;
/**
 * How long submit waits for `window.LeadiD.token` after the disclosure node
 * is in the document. The campaign script requests that token only once it
 * is running.
 */
export const JORNAYA_TOKEN_WAIT_MS = 5000;
export const JORNAYA_DISCLOSURE_NODE_ID = 'leadid_tcpa_disclosure';
const JORNAYA_SRC_MATCH =
  'script[src*="create.lidstatic.com/campaign/1a8b1fee-893f-e8f5-901a-5d12b2e0d47b.js"]';
/** A second submit in this window reuses an exhausted Jornaya wait. */
const JORNAYA_WAIT_REUSE_MS = 15000;
const POLL_MS = 100;

type JornayaWindow = Window & {
  LeadiD?: {
    token?: string;
  };
};

let trustedFormCertUrl = '';
let disclosureFirstSeenAt = 0;
let jornayaWaitExhaustedAt = 0;

const TRUSTED_FORM_FIELD = 'xxTrustedFormCertUrl';

export function rememberTrustedFormCertUrl(token: string): void {
  if (token) trustedFormCertUrl = token;
}

export function readTrustedFormCertUrl(): string {
  if (trustedFormCertUrl) return trustedFormCertUrl;
  if (typeof document === 'undefined') return '';
  const field = document.querySelector<HTMLInputElement>(
    `input[name="${TRUSTED_FORM_FIELD}"]`,
  );
  return field?.value ?? '';
}

/** Test helper. Production code latches the cert for the page lifetime. */
export function resetTrustedFormCertUrl(): void {
  trustedFormCertUrl = '';
}

/** Test helper. Clears the disclosure clock and the exhausted-wait reuse. */
export function resetJornayaTokenWait(): void {
  disclosureFirstSeenAt = 0;
  jornayaWaitExhaustedAt = 0;
}

export function waitForLeadToken(
  read: () => string,
  timeoutMs = LEAD_TOKEN_WAIT_MS,
): Promise<string> {
  const existing = read();
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const started = Date.now();
    const timer = setInterval(() => {
      const value = read();
      if (value || Date.now() - started >= timeoutMs) {
        clearInterval(timer);
        resolve(read());
      }
    }, POLL_MS);
  });
}

export function jornayaDisclosurePresent(): boolean {
  return (
    typeof document !== 'undefined' &&
    document.getElementById(JORNAYA_DISCLOSURE_NODE_ID) !== null
  );
}

export type JornayaTokenContext = {
  schoolSlug?: string;
  organizationId?: string;
};

function noteDisclosureSeen(now = Date.now()): void {
  if (!disclosureFirstSeenAt && jornayaDisclosurePresent()) {
    disclosureFirstSeenAt = now;
  }
}

function reportMissingJornayaToken(context?: JornayaTokenContext): void {
  noteDisclosureSeen();
  newRelicNoticeError(new Error('Jornaya token missing at lead submit'), {
    source: 'waitForJornayaToken',
    disclosurePresent: jornayaDisclosurePresent(),
    scriptTagPresent:
      typeof document !== 'undefined' &&
      document.querySelector(JORNAYA_SRC_MATCH) !== null,
    leadiDDefined:
      typeof window !== 'undefined' &&
      Boolean((window as JornayaWindow).LeadiD),
    disclosureAgeMs: disclosureFirstSeenAt
      ? Date.now() - disclosureFirstSeenAt
      : -1,
    ...(context?.schoolSlug ? { schoolSlug: context.schoolSlug } : {}),
    ...(context?.organizationId
      ? { organizationId: context.organizationId }
      : {}),
  });
}

/**
 * Waits for a Jornaya token without holding every submit for the token budget.
 * The disclosure node is rendered only after offering detail supplies TCPA
 * copy. Until that node exists the campaign script has not started, so a
 * flat token timeout expires before GenerateToken runs and the lead goes
 * out with an empty id. Once the node is present, wait for the token the
 * script requests. If the node never appears, stop at the disclosure budget.
 * A follow-up submit reuses an exhausted wait instead of blocking again.
 */
export function waitForJornayaToken(
  fallback: string,
  context?: JornayaTokenContext,
): Promise<string> {
  const read = () => readJornayaToken() || fallback;
  const existing = read();
  if (existing) {
    jornayaWaitExhaustedAt = 0;
    return Promise.resolve(existing);
  }

  if (
    jornayaWaitExhaustedAt &&
    Date.now() - jornayaWaitExhaustedAt < JORNAYA_WAIT_REUSE_MS
  ) {
    return Promise.resolve('');
  }

  return new Promise((resolve) => {
    const started = Date.now();
    noteDisclosureSeen(started);
    let tokenDeadline = jornayaDisclosurePresent()
      ? started + JORNAYA_TOKEN_WAIT_MS
      : 0;

    let timer: ReturnType<typeof setInterval>;
    const finish = (token: string) => {
      clearInterval(timer);
      if (token) {
        jornayaWaitExhaustedAt = 0;
      } else {
        jornayaWaitExhaustedAt = Date.now();
        reportMissingJornayaToken(context);
      }
      resolve(token);
    };

    timer = setInterval(() => {
      const token = read();
      if (token) {
        finish(token);
        return;
      }

      const now = Date.now();
      if (!tokenDeadline && jornayaDisclosurePresent()) {
        noteDisclosureSeen(now);
        tokenDeadline = now + JORNAYA_TOKEN_WAIT_MS;
      }

      const disclosureGaveUp =
        !tokenDeadline && now - started >= JORNAYA_DISCLOSURE_WAIT_MS;
      const tokenGaveUp = tokenDeadline !== 0 && now >= tokenDeadline;
      if (disclosureGaveUp || tokenGaveUp) {
        finish(read());
      }
    }, POLL_MS);
  });
}

export async function resolveLeadComplianceTokens(params: {
  jornayaToken: string;
  activeProspectToken: string;
  needsActiveProspectToken: boolean;
  /**
   * When false, return the token already on the page. Callers set this from
   * the lead schema: only a required `thirdPartyLeadId` that is not the
   * TrustedForm cert should pay the Jornaya wait.
   */
  waitForJornaya?: boolean;
  schoolSlug?: string;
  organizationId?: string;
}): Promise<{ jornayaToken: string; activeProspectToken: string }> {
  const shouldWaitForJornaya =
    params.waitForJornaya !== false && !params.needsActiveProspectToken;
  const [jornayaToken, activeProspectToken] = await Promise.all([
    shouldWaitForJornaya
      ? waitForJornayaToken(params.jornayaToken, {
          schoolSlug: params.schoolSlug,
          organizationId: params.organizationId,
        })
      : Promise.resolve(readJornayaToken() || params.jornayaToken),
    params.needsActiveProspectToken
      ? waitForLeadToken(
          () => readTrustedFormCertUrl() || params.activeProspectToken,
        )
      : Promise.resolve(readTrustedFormCertUrl() || params.activeProspectToken),
  ]);

  return { jornayaToken, activeProspectToken };
}

export function readJornayaToken(): string {
  if (typeof window === 'undefined') return '';
  return (window as JornayaWindow).LeadiD?.token ?? '';
}
